import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';
import clsx from 'clsx';
import { DISPATCH_EVENTS } from '../../../utils/constants/commonConstants';
/**
 * @Description Custom Tooltip component to display information on hover
 * @param content Tooltip message content
 * @param direction Tooltip position with respect to target element
 * @param children Elements for which the Tooltip is to be shown on hover
 * @param usePortal Whether to render tooltip in a React Portal (renders to document.body)
 *
 * ## When to use `usePortal={true}`:
 *
 * Use Portal rendering when the tooltip needs to escape CSS constraints:
 *
 * 1. **Parent has `overflow: hidden/auto/scroll`**
 *    - Tooltip gets clipped by scrollable containers
 *    - Example: Tables, modals, sidebars with scrolling
 *
 * 2. **Complex stacking contexts**
 *    - Tooltip appears behind other elements despite high z-index
 *    - Parent elements have `position: sticky`, `position: fixed`, or `z-index` values
 *    - Example: Sticky table headers, fixed navigation bars
 *
 * 3. **Nested positioned elements**
 *    - Tooltip is inside multiple layers of positioned elements
 *    - z-index doesn't work across stacking contexts
 *
 * ## When NOT to use `usePortal`:
 *
 * - Simple tooltips in normal document flow (default behavior is sufficient)
 * - No overflow or z-index issues present
 * - Performance-critical scenarios (portal adds minimal overhead but avoid if unnecessary)
 *
 * @example
 * // Regular tooltip (no portal needed)
 * <Tooltip content="Simple tooltip" direction="top">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // Portal tooltip (escapes table overflow)
 * <Tooltip content="Tooltip in table" direction="top" usePortal={true}>
 *   <TableCell>Content</TableCell>
 * </Tooltip>
 */
interface TooltipProps {
    content: string | React.ReactNode;
    direction: 'top' | 'right' | 'bottom' | 'left';
    isBenchmarking?: boolean;
    alwaysVisible?: boolean;
    inverted?: boolean;
    small?: boolean;
    containerFullWidth?: boolean;
    fitContent?: boolean;
    containerFullHeight?: boolean;
    children?: React.ReactNode;
    height?: string;
    style?: React.CSSProperties;
    isDealNotesTooltip?: boolean;
    isOverviewDetail?: boolean;
    hideDelay?: number;
    flexible?: boolean;
    contentCss?: React.CSSProperties;
    /**
     * Render tooltip in a React Portal to escape overflow clipping and stacking context issues.
     * Set to `true` when tooltip appears behind other elements or gets clipped by parent containers.
     * @default false
     */
    usePortal?: boolean;
}

const Tooltip = ({
    content = '',
    direction = 'top',
    children,
    alwaysVisible,
    inverted,
    small,
    containerFullWidth,
    fitContent,
    containerFullHeight,
    isBenchmarking,
    height = '',
    style,
    isDealNotesTooltip,
    isOverviewDetail,
    hideDelay = 0,
    flexible = false,
    contentCss,
    usePortal = false,
}: TooltipProps) => {
    const [active, setActive] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [clampTick, setClampTick] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (alwaysVisible) setActive(true);
    }, [alwaysVisible]);

    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []);

    /* 
        Listen for any other portal tooltip opening, and force-close ourselves.
        Decoupled cross-instance coordination via DOM event — no shared module state.
    */
    useEffect(() => {
        if (!usePortal) return;
        const handler = (e: Event) => {
            if ((e as CustomEvent).detail === wrapperRef.current) return; // it's us opening
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            setActive(false);
        };
        document.addEventListener(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, handler);
        return () => document.removeEventListener(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, handler);
    }, [usePortal]);

    // Keep portal tooltip inside the viewport on all 4 edges.
    useLayoutEffect(() => {
        if (!active || !usePortal || !tooltipRef.current) return;
        const r = tooltipRef.current.getBoundingClientRect();
        const PAD = 8;
        const dx = Math.max(PAD - r.left, Math.min(0, window.innerWidth - PAD - r.right));
        const dy = Math.max(PAD - r.top, Math.min(0, window.innerHeight - PAD - r.bottom));
        if (dx || dy) setTooltipPosition((p) => ({ top: p.top + dy, left: p.left + dx }));
    }, [active, usePortal, clampTick]);

    const showTip = () => {
        if (alwaysVisible) return;
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        // Portal rendering: Calculate absolute position relative to viewport
        // This is needed because portal renders to document.body, outside the normal DOM hierarchy
        if (usePortal && wrapperRef.current) {
            document.dispatchEvent(new CustomEvent(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, { detail: wrapperRef.current }));

            const rect = wrapperRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.top + window.scrollY, // Account for page scroll
                left: rect.left + rect.width / 2 + window.scrollX, // Center horizontally
            });
            setClampTick((t) => t + 1); // Force viewport clamp to re-run for the new position
        }

        setActive(true);
    };

    const hideTip = () => {
        if (alwaysVisible) return;
        if (hideDelay > 0) {
            hideTimeoutRef.current = setTimeout(() => {
                setActive(false);
            }, hideDelay);
        } else {
            setActive(false);
        }
    };

    const renderTooltipContent = () => {
        const tooltipStyles: React.CSSProperties = {
            color: inverted ? 'white' : '#979797',
            wordBreak: 'break-word',
            whiteSpace: inverted ? 'nowrap' : 'pre-wrap',
            width: fitContent ? 'fit-content' : '',
            minWidth: small ? '80px' : '200px',
            height: height || 'fit-content',
            overflowY: height ? 'auto' : 'hidden',
            overflowX: 'hidden',
            display: content ? 'inline-block' : 'none',
            zIndex: 1000,
            scrollbarWidth: isOverviewDetail ? 'none' : 'auto',
            ...style,
        };

        // Portal-specific positioning: Use fixed positioning to escape parent constraints
        // This allows the tooltip to appear above overflow:hidden containers and complex stacking contexts
        if (usePortal) {
            tooltipStyles.position = 'fixed'; // Fixed relative to viewport, not parent
            tooltipStyles.top = `${tooltipPosition.top}px`; // Absolute position from viewport top
            tooltipStyles.left = `${tooltipPosition.left}px`; // Absolute position from viewport left
            tooltipStyles.zIndex = 999999; // Very high z-index to appear above all elements
            tooltipStyles.transform = 'translate(-50%, calc(-100% - 10px))'; // Center and position above
        }

        return (
            <div
                ref={tooltipRef}
                className={`${isDealNotesTooltip && direction === 'bottom' ? 'DealNotesTooltipBottom' : ''} ${
                    isBenchmarking ? 'BenchmarkTooltip' : ''
                } Tooltip-Tip ${direction}`}
                style={tooltipStyles}
            >
                <div className={clsx('Content', { OverviewDetail: isOverviewDetail })} style={contentCss}>
                    {content}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={wrapperRef}
            className="Tooltip-Wrapper"
            onMouseEnter={showTip}
            onMouseLeave={hideTip}
            style={{
                width: containerFullWidth ? '100%' : '',
                height: containerFullHeight ? '100%' : '',
            }}
        >
            {children}
            {/* Normal rendering: Tooltip is a child of the wrapper (respects parent CSS) */}
            {active && !usePortal && renderTooltipContent()}
            {/* Portal rendering: Tooltip renders to document.body (escapes parent CSS constraints) */}
            {active && usePortal && createPortal(renderTooltipContent(), document.body)}
        </div>
    );
};

export default Tooltip;
